import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Check } from "lucide-react";
import { SEO } from "./SEO";
import { LeadForm } from "./LeadForm";
import { IMAGES } from "../../lib/medskin-data";
import { PILLARS } from "../../lib/pillar-data";

const TIMMA = "https://timma.no/salong/medskin-by";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const TreatmentPage = ({ data }) => {
  const canonical = `https://medskinby.no/behandlinger/${data.slug}`;
  const related = (data.relatedIssues || [])
    .map((s) => PILLARS[s])
    .filter(Boolean);

  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/media/treatment/${data.slug}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        if (cancelled) return;
        setUploadedPhotos(
          items.map((m) => ({
            src: `${BACKEND}${m.url}`,
            alt: m.alt || "Ekte kunde hos Medskinby",
            caption: m.caption || "",
          }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [data.slug]);

  const clientPhotos = [
    ...uploadedPhotos,
    ...(data.images?.clientPhotos || []),
  ];

  return (
    <>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        canonical={canonical}
        image={IMAGES.about}
      />

      <section className="pt-32 md:pt-40 pb-16 md:pb-24" data-testid={`treatment-hero-${data.slug}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-10">
            <Link to="/" className="hover:text-[#C4A981]">Hjem</Link>
            <ChevronRight size={12} strokeWidth={1.2} />
            <span className="text-[#2C2C2A]">{data.category}</span>
            <ChevronRight size={12} strokeWidth={1.2} />
            <span className="text-[#2C2C2A]">{data.h1.replace(" i Oslo", "")}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-[#C4A981]"></span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">{data.category}</span>
              </div>
              <h1 className="font-serif text-[2.5rem] md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#2C2C2A] font-light">
                {data.h1}
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[#635D56] font-light leading-relaxed">{data.lead}</p>
              <div className="mt-10">
                <a
                  href={TIMMA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-between gap-6 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] transition-all duration-500 rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em]"
                  data-testid={`treatment-${data.slug}-book-btn`}
                >
                  Book {data.h1.split(" ")[0].toLowerCase()}
                  <span className="rounded-full bg-[#C4A981] p-2.5 group-hover:rotate-45 transition-transform duration-500">
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  </span>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2 }}
              className="lg:col-span-5"
            >
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
                <img
                  src={data.images?.hero || IMAGES.about}
                  alt={data.h1}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F7F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            {data.paragraphs.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-[#2C2C2A] font-light leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="lg:col-span-5 bg-white border border-[#E5E0DA] rounded-3xl p-8 md:p-10">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C4A981] mb-5">{data.forWhomTitle || "Egnet for"}</div>
            <ul className="space-y-4">
              {data.forWhom.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check size={16} strokeWidth={1.5} className="text-[#4A4238] mt-1 shrink-0" />
                  <span className="text-sm md:text-base text-[#2C2C2A] font-light leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {data.packages && data.packages.length > 0 && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]" data-testid={`treatment-${data.slug}-packages`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#C4A981]"></span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Behandlingsmeny</span>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light">
                  Våre <em className="italic text-[#4A4238]">signaturbehandlinger</em>.
                </h2>
              </div>
              <p className="lg:col-span-5 text-base md:text-lg text-[#635D56] font-light leading-relaxed">
                Hver protokoll er utviklet i klinikken og tilpasses din hud, ditt mål og din anledning.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {data.packages.map((pkg, i) => (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="relative bg-white border border-[#E5E0DA] rounded-3xl p-8 md:p-10 flex flex-col hover:border-[#C4A981] hover:shadow-[0_20px_50px_-25px_rgba(74,66,56,0.25)] transition-all duration-500"
                  data-testid={`package-${i}`}
                >
                  {pkg.badge && (
                    <span className="absolute -top-3 left-8 inline-flex items-center bg-[#C4A981] text-[#2C2C2A] rounded-full px-4 py-1 text-[10px] uppercase tracking-[0.22em]">
                      {pkg.badge}
                    </span>
                  )}

                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-serif text-sm text-[#C4A981]">0{i + 1}</span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#635D56]">{pkg.duration}</span>
                  </div>

                  <h3 className="font-serif text-2xl md:text-[1.6rem] text-[#2C2C2A] leading-tight">
                    {pkg.name}
                  </h3>
                  {pkg.subtitle && (
                    <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#635D56]">{pkg.subtitle}</div>
                  )}

                  <div className="mt-6 flex items-baseline gap-3">
                    {pkg.price ? (
                      <>
                        <span className="font-serif text-3xl md:text-4xl text-[#2C2C2A]">{pkg.price}</span>
                        {pkg.originalPrice && (
                          <span className="text-sm text-[#635D56]/70 line-through">{pkg.originalPrice}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-[#635D56] font-light italic">Se pris ved booking</span>
                    )}
                  </div>

                  <p className="mt-6 text-sm md:text-[0.95rem] text-[#635D56] font-light leading-relaxed flex-1">
                    {pkg.description}
                  </p>

                  <a
                    href={TIMMA}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-8 inline-flex items-center justify-between gap-4 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] transition-all duration-500 rounded-full pl-6 pr-2 py-2.5 text-[11px] uppercase tracking-[0.22em] w-fit"
                    data-testid={`package-${i}-book-btn`}
                  >
                    Book behandling
                    <span className="rounded-full bg-[#C4A981] p-2 group-hover:rotate-45 transition-transform duration-500">
                      <ArrowUpRight size={12} strokeWidth={1.5} />
                    </span>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.causes && (
        <section className="py-20 md:py-28 bg-[#EBE5DF]/40" data-testid={`treatment-${data.slug}-causes`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#C4A981]"></span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Bakgrunn</span>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light">
                  {data.causes.title}
                </h2>
              </div>
              {data.causes.lead && (
                <p className="lg:col-span-5 text-base md:text-lg text-[#635D56] font-light leading-relaxed">
                  {data.causes.lead}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {data.causes.items.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-white border border-[#E5E0DA] rounded-3xl p-7 md:p-8"
                >
                  <span className="font-serif text-sm text-[#C4A981]">0{i + 1}</span>
                  <h3 className="mt-6 font-serif text-xl md:text-2xl text-[#2C2C2A] leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-[#635D56] font-light leading-relaxed">{c.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.processSteps && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]" data-testid={`treatment-${data.slug}-process`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#C4A981]"></span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Slik gjør du det</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light">
                {data.processSteps.title}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {data.processSteps.steps.map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`p-8 md:p-10 rounded-3xl border border-[#E5E0DA] ${i === 1 ? "bg-[#4A4238] text-[#F7F6F3]" : "bg-white"}`}
                >
                  <span className={`font-serif text-4xl md:text-5xl ${i === 1 ? "text-[#C4A981]" : "text-[#C4A981]"}`}>0{i + 1}</span>
                  <h3 className={`mt-8 font-serif text-xl md:text-2xl leading-tight ${i === 1 ? "text-[#F7F6F3]" : "text-[#2C2C2A]"}`}>
                    {step.title}
                  </h3>
                  <p className={`mt-4 text-sm md:text-[0.95rem] font-light leading-relaxed ${i === 1 ? "text-[#D9D0C5]" : "text-[#635D56]"}`}>
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.images?.gallery && data.images.gallery.length > 0 && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]" data-testid={`treatment-${data.slug}-gallery`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Galleri</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light max-w-3xl mb-12">
              Inn i <em className="italic text-[#4A4238]">klinikken</em>.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
              {data.images.gallery.map((img, i) => (
                <motion.figure
                  key={img.src}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`relative overflow-hidden rounded-[1.5rem] md:rounded-[2rem] group ${
                    i === 0
                      ? "md:col-span-7 aspect-[4/3]"
                      : i === 1
                      ? "md:col-span-5 aspect-[4/3]"
                      : "md:col-span-6 aspect-[5/4]"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                  {img.caption && (
                    <figcaption className="absolute bottom-4 left-4 bg-[#F7F6F3]/85 backdrop-blur-md rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#2C2C2A]">
                      {img.caption}
                    </figcaption>
                  )}
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {clientPhotos.length > 0 && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]" data-testid={`treatment-${data.slug}-clients`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#C4A981]"></span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Ekte kunder</span>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light">
                  Smil vi har vært <em className="italic text-[#4A4238]">med på å skape</em>.
                </h2>
              </div>
              <p className="lg:col-span-5 text-base md:text-lg text-[#635D56] font-light leading-relaxed">
                Ekte kunder, ekte resultater. Publisert med samtykke.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {clientPhotos.map((img, i) => (
                <motion.figure
                  key={img.src}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="relative overflow-hidden rounded-2xl md:rounded-[1.5rem] group aspect-[3/4]"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  />
                  {img.caption && (
                    <figcaption className="absolute bottom-3 left-3 bg-[#F7F6F3]/85 backdrop-blur-md rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#2C2C2A]">
                      {img.caption}
                    </figcaption>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2A]/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.images?.result && (
        <section className="py-20 md:py-28 bg-[#EBE5DF]/40" data-testid={`treatment-${data.slug}-result`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#C4A981]"></span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">{data.images.result.label || "Resultat"}</span>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light">
                  Synlig glød – <em className="italic text-[#4A4238]">umiddelbart</em>.
                </h2>
              </div>
              <p className="lg:col-span-5 text-base md:text-lg text-[#635D56] font-light leading-relaxed">
                Resultater varierer fra person til person. Hudens generelle struktur, hydrering og glød forbedres allerede etter første behandling.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-[2rem] md:rounded-[2.5rem] overflow-hidden border border-[#E5E0DA]"
            >
              <img
                src={data.images.result.src}
                alt={data.images.result.alt}
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="py-20 md:py-28 bg-[#EBE5DF]/40">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Hjelper med</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light max-w-3xl">
              Hudutfordringer denne behandlingen <em className="italic text-[#4A4238]">løser</em>.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/hudutfordringer/${r.slug}`}
                  className="group block bg-white p-8 rounded-3xl border border-[#E5E0DA] hover:border-[#C4A981] transition-all duration-500"
                  data-testid={`treatment-related-${r.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#635D56]">Hudutfordring</span>
                    <ArrowUpRight size={16} strokeWidth={1.2} className="text-[#C4A981] group-hover:rotate-45 transition-transform" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-[#2C2C2A] leading-tight">{r.h1}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.faq && data.faq.length > 0 && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Ofte stilte spørsmål</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light mb-12">Spørsmål &amp; svar.</h2>
            <div className="divide-y divide-[#E5E0DA] border-y border-[#E5E0DA]">
              {data.faq.map((f, i) => (
                <details key={i} className="group py-6">
                  <summary className="flex items-start justify-between gap-6 list-none cursor-pointer">
                    <span className="font-serif text-xl md:text-2xl text-[#2C2C2A]">{f.q}</span>
                    <span className="text-[#C4A981] text-2xl leading-none group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <p className="mt-4 text-base text-[#635D56] font-light leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <LeadForm treatmentTitle={data.h1.replace(" i Oslo", "")} treatmentSlug={data.slug} />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-[2rem] md:rounded-[2.5rem] bg-[#4A4238] px-8 py-16 md:px-16 md:py-20 text-center">
            <h2 className="font-serif text-3xl md:text-5xl text-[#F7F6F3] leading-[1.1] tracking-tight font-light max-w-3xl mx-auto">
              Book din behandling i Oslo.
            </h2>
            <a
              href={TIMMA}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-4 bg-[#C4A981] text-[#2C2C2A] hover:bg-[#F7F6F3] rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em] transition-all duration-500"
            >
              Book time
              <span className="rounded-full bg-[#4A4238] text-[#F7F6F3] p-2.5">
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};
